import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, dt, ROLE_LABEL } from "@/lib/api";
import { PageHeader } from "@/components/tam/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", email: "", password: "", role: "user", account_owner: "" };

function UserDialog({ owners, initial, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial || EMPTY);
  const edit = !!initial;
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, account_owner: f.account_owner === "all" ? "" : f.account_owner };
      if (edit) await api.patch(`/users/${initial.id}`, { name: body.name, role: body.role, account_owner: body.account_owner, password: body.password || null });
      else await api.post("/users", { ...body, account_owner: body.account_owner || null });
      toast.success(edit ? "User updated" : "User created");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF({ ...(initial || EMPTY), password: "" }); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{edit ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="user-form">
          <div className="space-y-1.5"><Label>Name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input required type="email" disabled={edit} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></div>
          <div className="space-y-1.5"><Label>{edit ? "New password (optional)" : "Password"}</Label><Input required={!edit} type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" /></div>
          <div className="space-y-1.5"><Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select></div>
          {f.role === "user" && <div className="space-y-1.5"><Label>Restrict to sheet (account manager)</Label>
            <Select value={f.account_owner || "all"} onValueChange={(v) => setF({ ...f, account_owner: v })}>
              <SelectTrigger data-testid="user-owner-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All accounts</SelectItem>{owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select></div>}
          <Button type="submit" className="w-full bg-[#D9232A] hover:bg-[#B81920]" data-testid="user-save-btn">Save</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [owners, setOwners] = useState([]);
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); api.get("/meta").then((r) => setOwners(r.data.owners)); }, [load]);
  const toggle = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users" subtitle="Managers create accounts and assign roles. Users only log progress.">
        <UserDialog owners={owners} onSaved={load} trigger={<Button className="bg-[#D9232A] hover:bg-[#B81920]" data-testid="add-user-btn">Add user</Button>} />
      </PageHeader>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="users-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 text-left"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Scope</th><th className="px-4 py-3">Created</th><th className="px-4 py-3">Active</th><th /></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td><td className="px-4 py-3">{u.email}</td><td className="px-4 py-3">{ROLE_LABEL[u.role]}</td>
                <td className="px-4 py-3 text-xs">{u.account_owner || "All"}</td><td className="px-4 py-3 text-xs">{dt(u.created_at)}</td>
                <td className="px-4 py-3"><Switch checked={u.active !== false} onCheckedChange={() => toggle(u)} data-testid={`user-active-${u.email}`} /></td>
                <td className="px-4 py-3 text-right"><UserDialog owners={owners} initial={{ ...u, account_owner: u.account_owner || "all" }} onSaved={load} trigger={<Button size="sm" variant="outline" data-testid={`edit-user-${u.email}`}>Edit</Button>} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
